import type { ReactNode } from 'react';
import type { UseGallerySelectionResult } from '../hooks/useGallerySelection';
import type {
  DownloadableItem,
  MediaDownloadResult,
} from '../hooks/useMediaDownload';

export interface GalleryToolbarDownloadBinding {
  /** The currently-selected items, mapped to the shared shape the helper
   *  understands. Order is the order they'll appear inside the ZIP; the
   *  worker sorts by created_at DESC regardless. */
  selectedItems: DownloadableItem[];
  downloadOne: (item: DownloadableItem) => Promise<void>;
  downloadZip: (items: DownloadableItem[]) => Promise<MediaDownloadResult>;
  busy: boolean;
  error: string | null;
}

interface Props {
  onNew?: () => void;
  newLabel?: string;
  selection?: UseGallerySelectionResult;
  /** All current item ids so "Select all" can target them. */
  allIds?: string[];
  /** Optional extra controls on the right side (e.g. SpeechTabs + ZIP button). */
  extras?: ReactNode;
  /** Stage 27: selection-driven Download flow. When selectMode is active
   *  AND selected.size > 0, a "⬇ Download N" button appears. N=1 calls
   *  downloadOne(); N≥2 calls downloadZip(); N>500 disables the button. */
  download?: GalleryToolbarDownloadBinding;
}

const ZIP_MAX_ITEMS = 500;

/**
 * Stage 12 (A6 + D1): shared gallery toolbar used by Images, Music, Speech
 * and Video gallery pages. Left side: "+ New …" button. Middle: multi-select
 * controls (enter / leave select mode, N selected, delete selected, select
 * all, clear). Stage 27: also renders a Download button bound to the
 * selection. Right side: gallery-specific extras.
 */
export function GalleryToolbar({
  onNew,
  newLabel = '+ New',
  selection,
  allIds,
  extras,
  download,
}: Props): JSX.Element {
  const selectedCount = selection?.selected.size ?? 0;
  const showDownload =
    download !== undefined &&
    selection?.selectMode === true &&
    selectedCount > 0;

  const firstItem = download?.selectedItems[0];
  const singleReady =
    selectedCount === 1 &&
    firstItem !== undefined &&
    firstItem.status === 'ready' &&
    firstItem.signedUrl != null;

  const overCap = selectedCount > ZIP_MAX_ITEMS;

  const downloadLabel = (() => {
    if (download?.busy) {
      return selectedCount === 1 ? 'Downloading…' : 'Bundling…';
    }
    if (overCap) return `⬇ Download ${selectedCount} (too many)`;
    if (selectedCount === 1) return '⬇ Download';
    return `⬇ Download ${selectedCount} (ZIP)`;
  })();

  const downloadDisabled =
    (download?.busy ?? false) ||
    selectedCount === 0 ||
    overCap ||
    (selectedCount === 1 && !singleReady);

  const downloadTitle = (() => {
    if (overCap) return 'Maximum 500 items per ZIP. Narrow your selection.';
    if (selectedCount === 1 && !singleReady) return 'File is not ready yet.';
    if (selectedCount === 1) return 'Download the selected file';
    return `Bundle ${selectedCount} selected items into a ZIP`;
  })();

  const onDownloadClick = async (): Promise<void> => {
    if (!download || selectedCount === 0 || overCap) return;
    if (selectedCount === 1 && firstItem) {
      await download.downloadOne(firstItem);
      return;
    }
    await download.downloadZip(download.selectedItems);
  };

  return (
    <div className="gallery-toolbar">
      {onNew ? (
        <button
          type="button"
          className="btn-primary"
          onClick={onNew}
          title={newLabel}
        >
          {newLabel}
        </button>
      ) : null}
      {selection ? (
        <>
          <button
            type="button"
            onClick={selection.toggleSelectMode}
            aria-pressed={selection.selectMode}
          >
            {selection.selectMode ? 'Cancel select' : 'Select'}
          </button>
          {selection.selectMode ? (
            <>
              {allIds && allIds.length > 0 ? (
                <button
                  type="button"
                  onClick={() =>
                    selection.selected.size === allIds.length
                      ? selection.clearSelected()
                      : selection.selectAll(allIds)
                  }
                >
                  {selection.selected.size === allIds.length
                    ? 'Clear'
                    : `Select all (${allIds.length})`}
                </button>
              ) : null}
              <span className="muted small">
                {selection.selected.size} selected
              </span>
              <button
                type="button"
                className="btn-danger"
                disabled={
                  selection.deleting || selection.selected.size === 0
                }
                onClick={() => {
                  if (
                    confirm(`Delete ${selection.selected.size} item(s)?`)
                  ) {
                    void selection.deleteSelected();
                  }
                }}
              >
                {selection.deleting
                  ? 'Deleting…'
                  : `Delete ${selection.selected.size}`}
              </button>
              {showDownload ? (
                <button
                  type="button"
                  disabled={downloadDisabled}
                  aria-disabled={downloadDisabled}
                  aria-busy={download?.busy ?? false}
                  aria-label={
                    selectedCount === 1
                      ? 'Download selected item'
                      : `Download ${selectedCount} selected items as ZIP`
                  }
                  title={downloadTitle}
                  onClick={() => void onDownloadClick()}
                >
                  {downloadLabel}
                </button>
              ) : null}
            </>
          ) : null}
        </>
      ) : null}
      <div className="spacer" />
      {extras}
      {selection?.error ? (
        <div className="status-missing small" role="alert">
          {selection.error}
        </div>
      ) : null}
      {download?.error ? (
        <div className="status-missing small" role="alert">
          {download.error}
        </div>
      ) : null}
    </div>
  );
}
